/**
 * Change Business Address (Section 47(3) / PD 2/2017, M32017) mapper.
 * DCR + Form PD2 + Section 47(3) Notification 이 값을 공유 → 병합 payload 하나를 세 템플릿에 전달.
 */
import type { SignerEntry } from '@/features/AI-documents/types/docGen.types';
import { formatMyAddressLine } from '@/shared/utils/malaysianAddress';
import { RECORD_ITEMS } from '../components/changeBusinessAddress/changeBusinessAddressFields';

export interface ChangeBusinessAddressState {
  values: Record<string, string>;
  flags: Record<string, boolean>;
  choices: Record<string, string>;
  /** [[Signers]] — varKey: 'dcr' | 'pd2' | 's47' */
  signers: Record<string, SignerEntry[]>;
}

export function emptyChangeBusinessAddressState(): ChangeBusinessAddressState {
  const flags: Record<string, boolean> = {
    is_multiple_director_company: false,
    change_nature_of_business: false,
  };
  // records a~k 기본 포함
  for (const r of RECORD_ITEMS) flags[r.key] = true;
  return {
    values: {},
    flags,
    choices: { resolution_basis: 'constitution' },
    signers: {},
  };
}

/** profile / list 날짜 → ISO YYYY-MM-DD */
export function normalizeCbaDateToIso(raw: unknown): string {
  if (!raw) return '';
  if (raw instanceof Date && !Number.isNaN(raw.getTime())) {
    return `${raw.getFullYear()}-${String(raw.getMonth() + 1).padStart(2, '0')}-${String(raw.getDate()).padStart(2, '0')}`;
  }
  const s = String(raw).trim();
  if (!s) return '';
  let m = s.match(/^(\d{4})-(\d{1,2})-(\d{1,2})/);
  if (m) return `${m[1]}-${m[2].padStart(2, '0')}-${m[3].padStart(2, '0')}`;
  m = s.match(/^(\d{1,2})[/-](\d{1,2})[/-](\d{4})$/);
  if (m) return `${m[3]}-${m[2].padStart(2, '0')}-${m[1].padStart(2, '0')}`;
  const d = new Date(s);
  if (!Number.isNaN(d.getTime())) {
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  }
  return '';
}

/** ISO → 문서 표시용 DD/MM/YYYY */
export function formatCbaDateDisplay(raw: unknown): string {
  const iso = normalizeCbaDateToIso(raw);
  if (!iso) return '';
  const [y, mo, d] = iso.split('-');
  return `${d}/${mo}/${y}`;
}

/** ARTICLE_NO 전체 텍스트 → resolution_basis + clause 번호 */
export function applyCbaRegisteredClause(regClause: string): {
  basis: 'constitution' | 'articles' | 'third_schedule';
  clauseNo: string;
} | null {
  const s = String(regClause || '').trim();
  if (!s) return null;
  if (/third\s*schedule/i.test(s)) return { basis: 'third_schedule', clauseNo: '' };
  const m1 = s.match(/(?:Article|Clause)\s+([\w()]+)/i);
  const clauseNo = m1
    ? m1[1]
    : (!/article|clause|constitution|association/i.test(s)
      ? s
      : (s.match(/\b(\d+[\w()]*)/)?.[1] || ''));
  if (/articles?\s+of\s+association/i.test(s) || /\barticles?\b/i.test(s)) {
    return { basis: 'articles', clauseNo };
  }
  return { basis: 'constitution', clauseNo };
}

/** 조건 토글 → 템플릿 [[if X]] 불리언 */
export function expandFlags(state: ChangeBusinessAddressState): Record<string, boolean> {
  const f = state.flags || {};
  const c = state.choices || {};
  const basis = c.resolution_basis || 'constitution';
  const out: Record<string, boolean> = {
    is_multiple_director_company: !!f.is_multiple_director_company,
    is_sole_director_company: !f.is_multiple_director_company,
    resolution_basis_constitution: basis === 'constitution',
    resolution_basis_articles: basis === 'articles',
    resolution_basis_third_schedule: basis === 'third_schedule',
    change_nature_of_business: !!f.change_nature_of_business,
  };
  for (const r of RECORD_ITEMS) out[r.key] = f[r.key] !== false;
  return out;
}

export function buildChangeBusinessAddressVariables(state: ChangeBusinessAddressState): Record<string, any> {
  const out: Record<string, any> = {};
  for (const [k, v] of Object.entries(state.values || {})) {
    const raw = v ?? '';
    // old_business_address 만 한 줄로 합성(대문자). 신주소 구성 필드(line1/2/3·postcode·city·state·country)는
    //   개별 값이 훼손되지 않도록 원본 그대로 전달.
    out[k] = k === 'old_business_address' ? formatMyAddressLine(String(raw)) : raw;
  }
  Object.assign(out, expandFlags(state));

  const basis = state.choices?.resolution_basis || 'constitution';
  const clause = String(state.values?.constitution_clause_number || '').trim();
  out.resolution_basis_clause =
    basis === 'third_schedule'
      ? 'Paragraph 15 of the Third Schedule of the Companies Act 2016'
      : basis === 'articles'
        ? (clause
          ? `Article ${clause} of the Company's Constitution (Articles of Association)`
          : "the Company's Constitution (Articles of Association)")
        : (clause
          ? `Clause ${clause} of the Company's Constitution`
          : "the Company's Constitution");

  return out;
}
